/**
 * =============================================================
 * AADHAV ACADEMY – TOURNAMENT EXPENSES API
 * Role: Admin
 * Scope: Tournament Expense Management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */

const TOURNAMENT_EXPENSE_ENDPOINT =
  "/tournament/tournament_expenses.php";

/* =============================================================
   GET – Fetch Tournament Expenses
   Supports:
   - All Expenses
   - Filter by tournament_id
============================================================= */

export const getTournamentExpenses = async (
  { tournament_id = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      TOURNAMENT_EXPENSE_ENDPOINT,
      null,
      "Tournament Expenses - Fetch",
      {
        params: {
          tournament_id,
        },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch expenses",
        data: [],
      };
    }

    return {
      success: true,
      data: res.data || [],
      count: res.count || 0,
    };
  } catch (error) {
    console.error("Fetch Tournament Expenses Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
    };
  }
};

/* =============================================================
   GET – Fetch Single Expense
============================================================= */

export const getTournamentExpenseById = async (
  id,
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      `${TOURNAMENT_EXPENSE_ENDPOINT}?id=${id}`,
      null,
      "Tournament Expense - Fetch By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Expense not found",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error("Fetch Expense By ID Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Tournament Expense
   Supports FormData (Invoice Upload)
============================================================= */

export const createTournamentExpense = async (
  payload,
  options = {}
) => {
  try {
    const res = await apiCall(
      "POST",
      TOURNAMENT_EXPENSE_ENDPOINT,
      payload,
      "Tournament Expense - Create",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to create expense",
      };
    }

    return {
      success: true,
      expense_id: res.expense_id,
      message: res.message,
    };
  } catch (error) {
    console.error("Create Tournament Expense Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Update Tournament Expense
   Supports FormData (Invoice Upload)
============================================================= */

export const updateTournamentExpense = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Expense ID",
      };
    }

    const res = await apiCall(
      "POST",
      `${TOURNAMENT_EXPENSE_ENDPOINT}?id=${id}`,
      payload,
      "Tournament Expense - Update",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to update expense",
      };
    }

    return {
      success: true,
      expense_id: res.expense_id,
      invoice_file: res.invoice_file,
      message: res.message,
    };
  } catch (error) {
    console.error("Update Tournament Expense Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Tournament Expense
============================================================= */

export const deleteTournamentExpense = async (
  id,
  options = {}
) => {
  try {
    const res = await apiCall(
      "DELETE",
      `${TOURNAMENT_EXPENSE_ENDPOINT}?id=${id}`,
      null,
      "Tournament Expense - Delete",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to delete expense",
      };
    }

    return {
      success: true,
      message: res.message,
    };
  } catch (error) {
    console.error("Delete Tournament Expense Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};